/**
 * Pure per-frame state for an ability slot (no DOM): cooldown / recharge / GCD sweeps on the
 * sub-tick clock, plus interned timer labels. Everything writes into caller-owned objects, so
 * the per-frame path allocates nothing.
 */
import type { CastFailReason } from '@keystone/sim';
import { queueFraction } from '../../game/queueWindow';
import { TENTHS, intText } from './tokens';

/** How a slot reads at a glance. Order = priority when several apply. */
export type SlotUsable = 'ok' | 'nores' | 'oor' | 'unusable';

/** What an icon slot draws this frame (owned and reused by the caller). */
export interface SlotView {
  /** Dark cooldown / recharge sweep, 0..1 of the circle still to go (0 = none). */
  cdFrac: number;
  /** Remaining cooldown for the centre number (ms; 0 = no number). */
  cdMs: number;
  /** Locked out by the cooldown (desaturated). Charge abilities with a charge left are not. */
  onCooldown: boolean;
  /** Lighter GCD sweep, 0..1 (0 = none). Hidden while `onCooldown`. */
  gcdFrac: number;
  /** +castux: the spell-queue window's share of the GCD sweep, 0..1 of the circle (0 = none): drawn lighter. */
  gcdQ?: number;
  charges: number;
  maxCharges: number;
  usable: SlotUsable;
  casting: boolean;
  queued: boolean;
}

export function emptySlotView(): SlotView {
  return { cdFrac: 0, cdMs: 0, onCooldown: false, gcdFrac: 0, charges: 0, maxCharges: 1, usable: 'ok', casting: false, queued: false };
}

export interface CooldownInput {
  /** Sim-time "present" (hudNow: world.time + time since the last step). */
  now: number;
  /** world.time (the last step). */
  tickTime: number;
  /** queries.cooldownRemaining(player, ability) at world.time. */
  remainAtTick: number;
  /** Unit cooldown state for the ability, if any. */
  cd: { charges: number; maxCharges: number; rechargeAt: number | null; rechargeDurationMs: number } | undefined;
  /** Ability definition's cooldown. */
  defDurationMs: number | undefined;
  defCharges: number | undefined;
  /** Ability GCD (0 = off-GCD). */
  gcdMs: number;
  /** Active GCD window (sim-time ms); until <= start means none. */
  gcdStart: number;
  gcdUntil: number;
}

/** Fill `out`'s cooldown / charge / GCD fields. Sub-tick smooth: everything is measured at `now`. */
export function computeCooldown(i: CooldownInput, out: SlotView): SlotView {
  const sinceTick = Math.max(0, i.now - i.tickTime);
  const remain = Math.max(0, i.remainAtTick - sinceTick);
  const maxCharges = i.cd?.maxCharges ?? i.defCharges ?? 1;
  const charges = i.cd?.charges ?? maxCharges;
  const dur = i.cd?.rechargeDurationMs || i.defDurationMs || 1;
  const rechargeLeft = i.cd?.rechargeAt != null ? Math.max(0, i.cd.rechargeAt - i.now) : remain;
  // Charge abilities sweep the recharge without locking while a charge is left.
  const sweep = maxCharges > 1 ? rechargeLeft / dur : remain / dur;
  out.cdFrac = sweep > 0 ? Math.min(1, sweep) : 0;
  out.cdMs = remain;
  out.onCooldown = remain > 0;
  out.charges = charges;
  out.maxCharges = maxCharges;
  const gcdLeft = Math.max(0, i.gcdUntil - i.now);
  const gcdFrac = gcdLeft > 0 ? Math.min(1, gcdLeft / Math.max(1, i.gcdUntil - i.gcdStart)) : 0;
  out.gcdFrac = i.gcdMs > 0 && !out.onCooldown ? gcdFrac : 0;
  out.gcdQ = out.gcdFrac > 0 ? queueFraction(i.gcdUntil - i.gcdStart) : 0; // +castux
  return out;
}

/** Map a canCast failure to the slot's usability look. */
export function usableFromReason(ok: boolean, reason: CastFailReason | undefined): SlotUsable {
  if (ok || !reason) return 'ok';
  switch (reason) {
    case 'no_resource':
      return 'nores';
    case 'out_of_range':
    case 'not_facing':
      return 'oor';
    case 'condition_failed':
    case 'silenced':
    case 'school_locked':
    case 'cannot_act':
    case 'rooted':
      return 'unusable';
    default:
      return 'ok'; // busy reasons (GCD, casting, cooldown) are drawn by the sweeps
  }
}

// ---- interned timer labels ---------------------------------------------------------------
// A label is identified by an integer key; the caller compares keys and only touches the DOM
// when the key changes. Keys: 0 none, 1..99 tenths (0.1..9.9 s), 1000+s whole seconds, 2000+m minutes.

const MIN_STR: string[] = Array.from({ length: 200 }, (_, i) => `${i}m`);
const minText = (m: number): string => (m < MIN_STR.length ? MIN_STR[m]! : `${m}m`);

/** Cooldown label key: tenths under 3 s, whole seconds to 99, then minutes. */
export function cooldownKey(ms: number): number {
  if (!(ms > 0)) return 0;
  if (ms < 3000) return Math.max(1, Math.round(ms / 100));
  const s = Math.ceil(ms / 1000);
  return s > 99 ? 2000 + Math.ceil(s / 60) : 1000 + s;
}

/** Aura label key: tenths under 10 s, whole seconds under 60, then minutes. */
export function auraTimeKey(ms: number | null): number {
  if (ms === null || !(ms > 0)) return 0;
  if (ms < 10000) return Math.max(1, Math.min(99, Math.round(ms / 100)));
  const s = ms / 1000;
  return s >= 60 ? 2000 + Math.ceil(s / 60) : 1000 + Math.ceil(s);
}

/** The label for a cooldownKey / auraTimeKey (interned, no allocation for common values). */
export function timerLabel(key: number): string {
  if (key <= 0) return '';
  if (key < 1000) return TENTHS[key] ?? (key / 10).toFixed(1);
  if (key < 2000) return intText(key - 1000);
  return minText(key - 2000);
}
